/**
 * How `/feedback` talks about the in-app feedback system (MASTER_PLAN M11.10,
 * FR-ADMIN-13, FR-FEEDBACK-01, rules §50, ADR-023).
 *
 * **The shapes and the rule come from `@platform/contracts`** — `FeedbackItem`,
 * `FeedbackComment`, the category and status unions, and `visibilityFor()`, which is the
 * *same function the API calls*. That matters more here than anywhere else in this app:
 * rules §50.10 and §50.11 say a private or security-sensitive report must not become
 * public, and a second copy of that decision in the UI is a second copy that can drift.
 *
 * What is left here is vocabulary — the words this screen puts on a category and a
 * status — and the two permission keys.
 */

import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_STATUSES,
  visibilityFor,
  type FeedbackCategory,
  type FeedbackStatus,
  type FeedbackVisibility,
} from '@platform/contracts';

export { FEEDBACK_CATEGORIES, FEEDBACK_STATUSES, visibilityFor };
export type { FeedbackCategory, FeedbackStatus, FeedbackVisibility };

/**
 * The two feedback keys, spelled exactly as SECURITY.md §5.1 spells them.
 *
 * They live here rather than in `lib/session/permissions.ts` because that file belongs to
 * the authentication workstream and reserved only the keys M2.7 needed. Folding these two
 * into `ADMIN_PERMISSIONS` is a one-line change whenever that file is next edited
 * (`Docs/ADMIN_SETTINGS_UI.md` open item 8); until then this is the single place either
 * key is written, so the nav, the screen and the tests cannot drift by a typo.
 */
export const FEEDBACK_PERMISSIONS = {
  submit: 'feedback.submit',
  moderate: 'feedback.moderate',
} as const;

export const FEEDBACK_CATEGORY_LABELS: Readonly<Record<FeedbackCategory, string>> = {
  bug: 'Bug',
  feature: 'Feature request',
  general: 'General feedback',
  security: 'Security report',
};

export const FEEDBACK_STATUS_LABELS: Readonly<Record<FeedbackStatus, string>> = {
  open: 'Open',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Shipped',
  declined: 'Declined',
  duplicate: 'Duplicate',
};

/**
 * `visibilityFor` under the name this app has always used for it.
 *
 * Kept as a re-export rather than a re-implementation: the submit dialog, the moderation
 * control, the mock and the server now all call one function (rules §50.10, §50.11).
 */
export const visibilityOf = visibilityFor;

export interface FeedbackFilters {
  /** `''` means every category. */
  category: FeedbackCategory | '';
  /** `''` means every status. */
  status: FeedbackStatus | '';
  /** Only what this account submitted. */
  mine: boolean;
}

export const EMPTY_FEEDBACK_FILTERS: FeedbackFilters = { category: '', status: '', mine: false };
